/**
 * Zutaten-Lexikon Web Component
 * Handles: letter nav activation, scroll-to-letter, active letter tracking, search filtering
 */
class ZutatenLexikon extends HTMLElement {
  connectedCallback() {
    this._buildLayout();

    this._letterNav = this.querySelector('[data-zl-letter-nav]');
    this._letterSections = [...this.querySelectorAll('.zl-letter-section[data-letter]')];
    this._searchInput = this.querySelector('[data-zl-search]');

    if (this._letterNav) this._initLetterNav();
    if (this._searchInput) this._initSearch();

    this.setAttribute('data-ready', '');
  }

  _buildLayout() {
    const letterSections = [...this.querySelectorAll('.zl-letter-section')];
    const sidebarCards = [...this.querySelectorAll('.zl-sidebar-card')];

    if (letterSections.length === 0 && sidebarCards.length === 0) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'page-width';

    const layout = document.createElement('div');
    layout.className = 'zl-layout';

    const main = document.createElement('main');
    main.className = 'zl-layout__main';

    const sidebar = document.createElement('aside');
    sidebar.className = 'zl-layout__sidebar';
    sidebar.setAttribute('aria-label', 'Zusätzliche Informationen');

    const sidebarInner = document.createElement('div');
    sidebarInner.className = 'zl-sidebar__inner';
    sidebar.appendChild(sidebarInner);

    letterSections.forEach((s) => main.appendChild(s));
    sidebarCards.forEach((c) => sidebarInner.appendChild(c));

    layout.appendChild(main);
    layout.appendChild(sidebar);
    wrapper.appendChild(layout);
    this.appendChild(wrapper);
  }

  _initLetterNav() {
    const activeLetters = new Set(
      this._letterSections.map((s) => s.dataset.letter.toUpperCase())
    );

    this._letterNav.querySelectorAll('.zl-letter-nav__btn[data-letter]').forEach((btn) => {
      if (activeLetters.has(btn.dataset.letter.toUpperCase())) {
        btn.disabled = false;
        btn.addEventListener('click', () => this._scrollToLetter(btn.dataset.letter));
      }
    });

    window.addEventListener('scroll', () => this._updateActiveBtn(), { passive: true });
    this._updateActiveBtn();
  }

  _scrollToLetter(letter) {
    const section = this.querySelector(
      `.zl-letter-section[data-letter="${letter.toUpperCase()}"]`
    );
    if (!section) return;

    const navWrapper = this._letterNav?.closest('.zl-letter-nav-wrapper');
    const offset = (navWrapper?.offsetHeight ?? 0) + 20;
    const y = section.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({ top: y, behavior: 'smooth' });
  }

  _updateActiveBtn() {
    if (!this._letterNav) return;

    const navWrapper = this._letterNav.closest('.zl-letter-nav-wrapper');
    const threshold = (navWrapper?.getBoundingClientRect().bottom ?? 0) + 8;

    let current = null;
    for (const section of this._letterSections) {
      if (section.getBoundingClientRect().top <= threshold) {
        current = section.dataset.letter.toUpperCase();
      }
    }

    this._letterNav.querySelectorAll('.zl-letter-nav__btn[data-letter]').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.letter.toUpperCase() === current);
    });

    // Scroll active button into view within the nav bar
    if (current) {
      const activeBtn = this._letterNav.querySelector(
        `.zl-letter-nav__btn[data-letter="${current}"]`
      );
      activeBtn?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }

  _normalize(str) {
    return str
      .toLowerCase()
      .replace(/ä/g, 'a')
      .replace(/ö/g, 'o')
      .replace(/ü/g, 'u')
      .replace(/ß/g, 'ss');
  }

  _initSearch() {
    this._searchInput.addEventListener('input', () => {
      this._filterIngredients(this._searchInput.value.trim());
    });
  }

  _filterIngredients(query) {
    const cards = this.querySelectorAll('.zl-ingredient-card');
    const sections = this.querySelectorAll('.zl-letter-section');
    const normalizedQuery = this._normalize(query);

    if (!query) {
      cards.forEach((c) => {
        const block = c.closest('.shopify-block') ?? c;
        block.style.display = '';
      });
      sections.forEach((s) => (s.style.display = ''));
      this._updateLetterNavVisibility(null);
      return;
    }

    cards.forEach((card) => {
      const name = this._normalize(
        card.querySelector('.zl-ingredient-card__name')?.textContent ?? ''
      );
      const block = card.closest('.shopify-block') ?? card;
      block.style.display = name.includes(normalizedQuery) ? '' : 'none';
    });

    // Hide letter sections that have no visible ingredients
    sections.forEach((section) => {
      const hasVisible = [...section.querySelectorAll('.zl-ingredient-card')].some((card) => {
        const block = card.closest('.shopify-block') ?? card;
        return block.style.display !== 'none';
      });
      section.style.display = hasVisible ? '' : 'none';
    });

    // Update letter nav buttons to reflect filtered state
    const visibleLetters = new Set(
      [...sections]
        .filter((s) => !s.hidden)
        .map((s) => s.dataset.letter.toUpperCase())
    );
    this._updateLetterNavVisibility(visibleLetters);
  }

  _updateLetterNavVisibility(visibleLetters) {
    if (!this._letterNav) return;

    this._letterNav.querySelectorAll('.zl-letter-nav__btn[data-letter]').forEach((btn) => {
      if (visibleLetters === null) {
        // Reset: re-enable all buttons that have sections
        const activeLetters = new Set(
          this._letterSections.map((s) => s.dataset.letter.toUpperCase())
        );
        btn.disabled = !activeLetters.has(btn.dataset.letter.toUpperCase());
      } else {
        btn.disabled = !visibleLetters.has(btn.dataset.letter.toUpperCase());
      }
    });
  }
}

customElements.define('zutaten-lexikon', ZutatenLexikon);
